import React, {useEffect, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import SimpleCard from "connectui/component/display/card/SimpleCard";
import {useTheme} from "connectui/theme/ThemeProvider";
import FlexContainer from "../../../../connectui/component/layout/FlexContainer";
import Text from "../../../../connectui/component/display/Text";
import Container from "../../../../connectui/component/layout/Container";

import Tooltip from "../../../../connectui/component/display/Tooltip";
import {
  BaseChartTooltip,
  LineChart,
} from "../../../../connectui/component/charts/";
import {getDashboardRevenueTimeSeries} from "redux/actions/dashboardActions";
import SvgIcon from "../../../../connectui/component/display/SvgIcon";
import ICONS from "../../../../connectui/icons/iconMap";
import DashboardEmptyStateCard from "./DashboardEmptyStateCard";
import TimeframeSelector, {INTERVAL_MAP} from "../input/TimeframeSelector";
import {useTranslation} from "react-i18next";

const MetricIndicator = ({
  title,
  value,
  seriesId,
  chartSeries,
  theme,
  helpTooltipKey,
}) => {
  const {t} = useTranslation();

  return (
    <FlexContainer
      flexDirection="column"
      sx={{
        position: "relative",
        paddingLeft: theme.spacing(3),
      }}
    >
      <Container
        sx={{
          position: "absolute",
          left: 0,
          height: "90%",
          width: "4px",
          borderRadius: "2px",
          backgroundColor:
            chartSeries.find((s) => s.id === seriesId)?.color ||
            (seriesId === "upsells"
              ? theme.colors.success
              : theme.colors.error),
        }}
      />
      <FlexContainer alignItems="center" gap={theme.spacing(2)}>
        <Text variant="textSM" color="textSecondary" weight="medium">
          {title}
        </Text>
        {helpTooltipKey && (
          <Tooltip tooltipContent={t(helpTooltipKey)}>
            <Container sx={{cursor: "help"}}>
              <SvgIcon
                component={ICONS["help-circle"]}
                sx={{
                  width: "14px",
                  height: "14px",
                  color: theme.colors.gray400,
                }}
              />
            </Container>
          </Tooltip>
        )}
      </FlexContainer>
      <FlexContainer alignItems="center" gap={theme.spacing(1)}>
        <Text variant="textXL" weight="semibold">
          {value}
        </Text>
      </FlexContainer>
    </FlexContainer>
  );
};

const RevenueCard = ({timeframe, currency = "USD", listingGroups = null}) => {
  const {theme} = useTheme();
  const dispatch = useDispatch();
  const [displayInterval, setDisplayInterval] = useState("By Day");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const {t} = useTranslation();

  const revenueTimeSeriesData = useSelector(
    (state) => state.dashboardReducer.revenueTimeSeriesData,
  );

  const RevenueCardTooltip = ({data, position, chartContainerRef}) => {
    if (!data || !data.items || !position) return null;

    const formatCurrency = (value, currency = "USD") => {
      const safeCurrency = currency || "USD";
      const numValue = parseFloat(value.replace(/[^0-9.-]/g, "")) || 0;
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: safeCurrency,
        maximumFractionDigits: 0,
      }).format(numValue);
    };

    const formattedItems = data.items.map((item) => ({
      ...item,
      value: formatCurrency(item.value, currency),
    }));

    return (
      <BaseChartTooltip
        position={position}
        color={data.items[0]?.color}
        items={formattedItems}
        chartContainerRef={chartContainerRef}
      />
    );
  };

  const {chartData, chartSeries, totalRevenue, upsellsRevenue, feesRevenue} =
    useMemo(() => {
      if (!revenueTimeSeriesData) {
        return {
          chartData: [],
          chartSeries: [],
          totalRevenue: 0,
          upsellsRevenue: 0,
          feesRevenue: 0,
        };
      }

      const summary = revenueTimeSeriesData.summary || {};

      return {
        chartData: revenueTimeSeriesData.dataPoints || [],
        chartSeries: revenueTimeSeriesData.series || [],
        totalRevenue: summary.total_revenue || 0,
        upsellsRevenue: summary.upsells_revenue || 0,
        feesRevenue: summary.fees_revenue || 0,
      };
    }, [revenueTimeSeriesData]);

  useEffect(() => {
    if (!timeframe) return;

    setLoading(true);
    setError(false);

    let isMounted = true;

    dispatch(
      getDashboardRevenueTimeSeries({
        start_date: timeframe.startDate,
        end_date: timeframe.endDate,
        listing_group_ids: listingGroups,
        display_currency: currency,
        interval: INTERVAL_MAP[displayInterval],
        onSuccess: () => {
          if (isMounted) setLoading(false);
        },
        onError: () => {
          if (isMounted) {
            setLoading(false);
            setError(true);
          }
        },
      }),
    );

    return () => {
      isMounted = false;
    };
  }, [
    timeframe?.startDate,
    timeframe?.endDate,
    displayInterval,
    dispatch,
    currency,
    listingGroups,
  ]);

  const formatCurrency = (value) => {
    const safeCurrency = currency || "USD";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: safeCurrency,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <SimpleCard
      customContent={
        <FlexContainer
          width={"100%"}
          alignItems={"flex-start"}
          flexDirection={"column"}
          gap={theme.spacing(1)}
        >
          <FlexContainer
            justifyContent={"space-between"}
            alignItems={"flex-start"}
            width={"100%"}
          >
            <FlexContainer alignItems="center" gap={theme.spacing(1)}>
              <Text
                variant={"textMD"}
                color={"textSecondary"}
                weight={"medium"}
              >
                {t("dashboard.revenue-timeseries.title")}
              </Text>
              <Tooltip tooltipContent={t("dashboard.revenue-timeseries.help")}>
                <Container sx={{cursor: "help"}}>
                  <SvgIcon
                    component={ICONS["help-circle"]}
                    sx={{
                      width: "14px",
                      height: "14px",
                      color: theme.colors.gray400,
                    }}
                  />
                </Container>
              </Tooltip>
            </FlexContainer>
            <FlexContainer gap={theme.spacing(2)} alignItems="center">
              <TimeframeSelector
                timeframe={timeframe}
                selectedInterval={displayInterval}
                onIntervalChange={setDisplayInterval}
              />
            </FlexContainer>
          </FlexContainer>

          {loading && (
            <Container
              skeleton={{enabled: true, width: "100%", height: "300px"}}
              sx={{width: "100%"}}
            />
          )}

          {!loading && !error && chartData.length === 0 && (
            <DashboardEmptyStateCard
              text={t("dashboard.revenue-timeseries.empty-state")}
              icon={ICONS["no-products"]}
              height="300px"
            />
          )}

          {!loading && !error && chartData.length > 0 && (
            <>
              <Text
                variant="displaySM"
                weight={"semibold"}
                sx={{marginBottom: theme.spacing(2)}}
              >
                {formatCurrency(totalRevenue)}
              </Text>

              <FlexContainer width="100%" gap={theme.spacing(5)}>
                <MetricIndicator
                  title={t("dashboard.revenue-timeseries.upsells-metric-title")}
                  value={formatCurrency(upsellsRevenue)}
                  seriesId="upsells"
                  chartSeries={chartSeries}
                  theme={theme}
                  helpTooltipKey="dashboard.revenue-timeseries.upsells-help"
                />

                <MetricIndicator
                  title={t("dashboard.revenue-timeseries.fees-metric-title")}
                  value={formatCurrency(feesRevenue)}
                  seriesId="fees"
                  chartSeries={chartSeries}
                  theme={theme}
                  helpTooltipKey="dashboard.revenue-timeseries.fees-help"
                />
              </FlexContainer>
            </>
          )}
          {!loading && !error && chartData.length > 0 ? (
            <Container height={250} width="100%" position="relative">
              <LineChart
                data={chartData}
                series={chartSeries}
                settings={{
                  height: 250,
                  padding: {top: 20, right: 20, bottom: 20, left: 40},
                  tooltip: {
                    component: RevenueCardTooltip,
                  },
                  grid: {
                    horizontal: true,
                    vertical: false,
                    color: theme.colors.gray300,
                  },
                  axis: {
                    x: {
                      tickCount: 6,
                    },
                    y: {
                      tickCount: 4,
                    },
                  },
                }}
              />
            </Container>
          ) : null}
        </FlexContainer>
      }
      sx={{
        height: "100%",
        width: "100%",
        backgroundColor: theme.colors.white,
      }}
    />
  );
};

export default RevenueCard;
